// Phase 84.63 — the "Board": To Do (planned) · In Progress · Review · Done.
// Gating: anyone can move a task into Review; only the Founder or that
// project's Lead can move a task from Review (or anywhere) to Done — see
// server.py _user_is_project_lead_or_founder / update_daily_task.
// Phase 101 — "Abandoned" is a 5th status (auto-flipped after 14 days of no
// activity, or set manually) but it's rendered as a SEPARATE strip, never
// one of the 4 active Kanban columns — see ABANDONED_STATUS below.
export const DAILY_TASK_STATUSES = ["planned", "in_progress", "review", "done"];
export const ABANDONED_STATUS = "abandoned";
// Phase 118 — "Rework": Founder/Lead sends a Done task back for quality
// fixes via the Task Hub's "Send Back" action. It's a real status value
// (validated server-side) but deliberately NOT one of the 4 Kanban columns
// above — it only gets its distinct purple treatment inside the Task Hub's
// "Active Tasks" list (pinned just below Delayed/Stalled).
export const REWORK_STATUS = "rework";
export const ALL_TASK_STATUSES = [...DAILY_TASK_STATUSES, ABANDONED_STATUS, REWORK_STATUS];

export const STATUS_LABEL = {
  planned: "To do",
  in_progress: "In progress",
  review: "Review",
  done: "Done",
  abandoned: "Abandoned",
  rework: "Rework",
};

// Tone tokens map to the studio's Tag palette (green/blue/orange/red/slate)
// so the badges match the rest of the app.
export const STATUS_TONE = {
  planned: "slate",
  in_progress: "blue",
  review: "purple",
  done: "green",
  abandoned: "red",
  rework: "purple",
};

// CSS classes for the Kanban column header + card left-border accent.
export const STATUS_ACCENT = {
  planned:     "border-slate-300  text-slate-600",
  in_progress: "border-blue-400   text-blue-700",
  review:      "border-purple-400 text-purple-700",
  done:        "border-emerald-400 text-emerald-700",
  abandoned:   "border-red-300 text-red-600",
  rework:      "border-purple-500 text-purple-700",
};

// "Xh Ym" (or "0h") from total_minutes — used by every task widget so the
// logged-time chip looks identical on the Hub, the Board and the Registry.
export function formatLoggedHours(totalMinutes) {
  const m = totalMinutes || 0;
  if (m === 0) return "0h";
  const h = Math.floor(m / 60);
  const mm = m % 60;
  if (h === 0) return `${mm}m`;
  return mm === 0 ? `${h}h` : `${h}h ${mm}m`;
}

export function isTerminal(status) {
  return status === "done";
}

// Phase 104 — Task Review SLA: a task sitting in "review" this many days
// without a decision is flagged stale so approvals don't go quiet.
export const TASK_REVIEW_SLA_DAYS = 3;
export function isReviewStale(task) {
  if (!task || task.status !== "review") return false;
  const hist = task.review_history || [];
  const since = hist[hist.length - 1]?.submitted_at || task.updated_at;
  if (!since) return false;
  return (Date.now() - new Date(since).getTime()) > TASK_REVIEW_SLA_DAYS * 86400000;
}

// Phase 111 — Home "High-Focus Zone": a task is Delayed once it's past its
// due date (and not Done/Abandoned), or Stalled once it started 2+ days ago
// and hasn't been touched since (still sitting untouched in To do/In
// progress). Both are pinned + highlighted red on the Task Hub.
export const STALLED_DAYS = 2;

export function isDelayed(task) {
  if (!task || !task.due_date) return false;
  if (task.status === "done" || task.status === ABANDONED_STATUS) return false;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return new Date(`${task.due_date}T00:00:00`) < today;
}

export function isStalled(task) {
  if (!task) return false;
  if (task.status !== "planned" && task.status !== "in_progress") return false;
  const started = task.start_date;
  if (!started) return false;
  const startedMs = new Date(`${started}${started.length <= 10 ? "T00:00:00" : ""}`).getTime();
  if (Number.isNaN(startedMs) || Date.now() - startedMs < STALLED_DAYS * 86400000) return false;
  // "not moved" — nothing has happened to it since it started.
  const lastTouch = task.updated_at || started;
  const touchMs = new Date(`${lastTouch}${lastTouch.length <= 10 ? "T00:00:00" : ""}`).getTime();
  return Number.isNaN(touchMs) ? true : Date.now() - touchMs >= STALLED_DAYS * 86400000;
}

export function isUrgent(task) {
  return isDelayed(task) || isStalled(task);
}

// Human-readable "due X" chip label. Returns { label, tone } or null when no date.
export function dueChip(due_date, status) {
  if (!due_date || status === "done") return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(`${due_date}T00:00:00`);
  const diff = Math.round((d - today) / (1000 * 60 * 60 * 24));
  if (diff < 0) return { label: `${Math.abs(diff)}d overdue`, tone: "red" };
  if (diff === 0) return { label: "Due today", tone: "orange" };
  if (diff === 1) return { label: "Due tomorrow", tone: "orange" };
  return { label: `Due in ${diff}d`, tone: "slate" };
}
